import React, { Component } from 'react';
import { withStyles } from '@material-ui/styles';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Container, Row, Col } from 'reactstrap';
import PageTitle from 'components/titles/PageTitle';
import PropTypes from 'prop-types';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';
import Button from 'components/buttons/Button';
import Modal from '@material-ui/core/Modal';
import Alert from '@material-ui/lab/Alert';
import { updateEmail, updateUser, updatePassword, getUser } from 'redux/actions/userActions';
import UpdateEmailModal from 'components/modals/UpdateEmailModal';
import UpdatePasswordModal from 'components/modals/UpdatePasswordModal';
import { requiredValidator } from 'utils/validators';
import RenderFormInput from 'components/forms/RenderFormInput';
import RenderSelectInput from 'components/forms/RenderSelectInput';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { get, isEmpty } from 'lodash';
import {
  countryList,
  DEFAULT_COUNTRY,
  MIN_DESKTOP_WIDTH,
  MAX_MEDIUM_SIZE,
  NOTIFICATION_NODE_STYLE,
} from 'constants/misc';
import NotificationSystem from 'rc-notification';
import BasicNotification from 'components/Notification';

const styles = theme => ({
  infoWrapper: {
    backgroundColor: theme.palette.secondary.background,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  heading: {
    fontSize: '32px',
    fontWeight: '600',
    lineHeight: '36px',
    letterSpacing: '0',
    marginBottom: '37px',
  },
  section: {
    padding: '24px 32px 24px 32px',
    backgroundColor: theme.palette.white,
    marginBottom: '20px',
    borderRadius: '8px',
    boxShadow: theme.shadows[3],
    [`@media screen and (max-width: ${MAX_MEDIUM_SIZE}px)`]: {
      width: '37rem',
    },
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      width: '25rem',
    },
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '-0.2px',
    marginBottom: '24px',
  },
  sectionDescription: {
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.2px',
    marginBottom: '16px',
  },
  sectionDescriptionBold: {
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0',
    fontWeight: '600',
    marginBottom: '16px',
  },
  personalInfoInputs: {
    display: 'flex',
    marginBottom: '16px',
  },
  inputBoxTitle: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0',
    lineHeight: '16px',
    marginBottom: '9px',
    color: theme.palette.gray.dark,
  },
  phoneNumber: {
    display: 'flex',
    width: '100%',
  },
  countryDropdown: {
    width: '8rem',
    marginRight: '4px',
  },
  warning: {
    margin: '16px 0',
  },
});

class MyAccount extends Component {
  constructor(props) {
    super();
    this.state = {
      infoChanged: false,
      isEmailModalOpen: false,
      isPasswordModalOpen: false,
      country: get(props.user, 'userphone.country', DEFAULT_COUNTRY),
    };
  }

  async componentDidMount() {
    // eslint-disable-next-line no-return-assign
    NotificationSystem.newInstance({}, n => this.notification = n);
  }

  componentWillUnmount() {
    this.notification.destroy();
  }

  async onSubmitEmailForm(formData) {
    try {
      await this.props.updateEmail(formData);
      await this.props.getUser();
      this.toggleEmailModal();
      this.showNotification('Email Updated Successfully.', 'success');
    } catch (error) {
      throw new SubmissionError(error.errors);
    }
  }

  async onSubmitPasswordForm(formData) {
    try {
      await this.props.updatePassword(formData);
      this.showNotification('Password Changed Successfully.', 'success');
    } catch (error) {
      throw new SubmissionError(error.errors);
    }
    this.togglePasswordModal();
  }

  handleInputChange = () => {
    this.setState({
      infoChanged: true,
    });
  }

  handleCountryChange = (countryObject) => {
    this.setState({
      infoChanged: true,
      country: countryObject.value,
    });
  }

  discardChanges = () => {
    this.setState({
      infoChanged: false,
    });
    this.props.reset();
  }

  toggleEmailModal = () => {
    this.setState({
      isEmailModalOpen: !this.state.isEmailModalOpen,
    });
  };

  togglePasswordModal = () => {
    this.setState({
      isPasswordModalOpen: !this.state.isPasswordModalOpen,
    });
  };

  showNotification = (message, errorType = 'success') => {
    this.notification.notice({
      content: <BasicNotification
        color={errorType}
        message={message}
      />,
      duration: 6,
      closable: false,
      style: NOTIFICATION_NODE_STYLE,
      className: 'alert--colored right-up',
    });
  }

  handleSubmit = async (formData) => {
    let number = get(formData, 'userphone.number');
    const country = get(formData, 'userphone.country.value', DEFAULT_COUNTRY);
    if (!isEmpty(number)) {
      try {
        number = parsePhoneNumber(number, country).formatNational();
      } catch (error) {
        throw new SubmissionError({
          userphone: {
            number: ['Invalid phone number.'],
          },
        });
      }
    }

    this.setState({
      infoChanged: false,
    });
    const user = {
      ...this.props.user,
      ...formData,
      userphone: {
        country,
        number,
      },
    };
    try {
      await this.props.updateUser(user);
      this.showNotification('Account Updated Successfully.', 'success');
    } catch (error) {
      throw new SubmissionError(error.errors);
    }
  }

  formatNumber = value => (
    value ? new AsYouType(this.state.country).input(value) : value
  )

  render() {
    const {
      classes, user, handleSubmit, pristine, submitting,
    } = this.props;
    return (
      <Container>
        <PageTitle>My Account</PageTitle>
        <Row className={classes.infoWrapper}>
          <Col md={12}>
            <form className={classes.section} onSubmit={handleSubmit(this.handleSubmit)}>
              <h3 className={classes.sectionTitle}>Contact Info</h3>
              <div className={classes.personalInfoInputs}>
                <div className="form__form-group mt-3 mr-3">
                  <span className={classes.inputBoxTitle}>First Name</span>
                  <div className="form__form-group-field">
                    <Field
                      name="first_name"
                      type="text"
                      component={RenderFormInput}
                      validate={[requiredValidator]}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
                <div className="form__form-group mt-3">
                  <span className={classes.inputBoxTitle}>Last Name</span>
                  <div className="form__form-group-field">
                    <Field
                      name="last_name"
                      type="text"
                      component={RenderFormInput}
                      validate={[requiredValidator]}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className={classes.personalInfoInputs}>
                <div className="form__form-group mt-3">
                  <span className={classes.inputBoxTitle}>Title</span>
                  <div className="form__form-group-field">
                    <Field
                      name="title"
                      component={RenderFormInput}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className={classes.personalInfoInputs}>
                <Row style={{ marginBottom: '1.25rem', marginLeft: '0', width: '100%' }}>
                  <Col className="form__form-group mt-3 p-0" md={2}>
                    <span className={classes.inputBoxTitle}>Cellphone Number</span>
                    <div className="form__form-group-field">
                      <Field
                        name="userphone.country"
                        component={RenderSelectInput}
                        options={countryList}
                        variant="outlined"
                        onChange={this.handleCountryChange}
                      />
                    </div>
                  </Col>
                  <Col className="form__form-group mt-3 pr-0" md={10}>
                    <span className={classes.inputBoxTitle}>&nbsp;</span>
                    <div className="form__form-group-field">
                      <Field
                        name="userphone.number"
                        component={RenderFormInput}
                        format={this.formatNumber}
                        onChange={this.handleInputChange}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
              {this.state.infoChanged ?
                <>
                  <hr />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="medium"
                      variant="textSecondary"
                      onClick={this.discardChanges}
                      disabled={pristine || submitting}
                      style={{ marginRight: '0.25rem' }}
                    >
                      Discard
                    </Button>
                    <Button
                      variant="containedPrimaryCTA"
                      size="medium"
                      type="submit"
                      disabled={pristine || submitting}
                    >
                      Save
                    </Button>
                  </div>
                </> : null}
            </form>

            <div className={classes.section}>
              <h3 className={classes.sectionTitle}>Account Email</h3>
              <div style={{ marginBottom: '1rem' }}>
                <span className={classes.sectionDescription}>Your current account email: </span>
                <span className={classes.sectionDescriptionBold}>{user.email}</span>
              </div>
              {user.unverified_email && !user.unverified_email.verified ?
                <div className={classes.warning}>
                  <Alert color="warning" severity="error">
                    Please confirm your email. <br />
                    We&apos;ve sent you an email to &lsquo;{user.unverified_email.email}&lsquo;
                    <br /> <br />
                    We continue using your old email: &lsquo;{user.email}&lsquo;
                    until you confirm the new one.
                  </Alert>
                </div> : null
              }
              <Button
                color="primary"
                variant="outlined"
                size="medium"
                onClick={this.toggleEmailModal}
              >
                Update Email
              </Button>
              <Modal
                open={this.state.isEmailModalOpen}
                onClose={this.toggleEmailModal}
                disableEnforceFocus
              >
                <UpdateEmailModal
                  onSubmit={data => this.onSubmitEmailForm(data)}
                  toggleEmailModal={this.toggleEmailModal}
                />
              </Modal>
            </div>

            <div className={classes.section}>
              <h3 className={classes.sectionTitle}>Account Password</h3>
              <div style={{ marginBottom: '1rem' }}>
                <span className={classes.sectionDescription}>Change password you use to login to: </span>
                <span className={classes.sectionDescriptionBold}>{user.email}</span>
              </div>
              <Button
                color="primary"
                variant="outlined"
                size="medium"
                onClick={this.togglePasswordModal}
              >
                Update Password
              </Button>
              <Modal
                open={this.state.isPasswordModalOpen}
                onClose={this.togglePasswordModal}
                disableEnforceFocus
              >
                <UpdatePasswordModal
                  onSubmit={data => this.onSubmitPasswordForm(data)}
                  togglePasswordModal={this.togglePasswordModal}
                />
              </Modal>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }
}

MyAccount.propTypes = {
  classes: PropTypes.element.isRequired,
  user: PropTypes.element.isRequired,
  updateUser: PropTypes.func.isRequired,
  updateEmail: PropTypes.func.isRequired,
  updatePassword: PropTypes.func.isRequired,
  getUser: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
};

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      initialValues: {
        first_name: state.user.first_name,
        last_name: state.user.last_name,
        title: state.user.title,
        userphone: {
          country: countryList.find(el => el.value === get(state.user, 'userphone.country', DEFAULT_COUNTRY)),
          number: get(state.user, 'userphone.number'),
        },
      },
      user: state.user,
    }),
    {
      updateUser, updatePassword, updateEmail, getUser,
    },
  ),
  reduxForm({
    form: 'updatePersonalAccountForm',
    enableReinitialize: true,
  }),
)(MyAccount);
